<template>
  <v-row>
    <v-col :cols="12">
      <sub-heading class="text-uppercase mb-1 text-disabled">
        {{ $t('product-detail-view.details.title') }}
      </sub-heading>
      <v-card class="pt-3 ">
        <v-card-subtitle
          v-if="product && product.description"
          class="text-subtitle-1"
        >
          {{ $t('product-detail-view.details.basics.title') }}
        </v-card-subtitle>
        <v-card-title v-if="product && product.brand.slug === '47'">
          <v-alert density="compact">
            <template v-slot:text>
              <b>NB: </b>{{ product.brand.name }} er dessverre et utgått varemerke og finnes stort sett ikke på lager
              lenger - men noen selger ut varebeholdningen sin.
            </template>
          </v-alert>
        </v-card-title>
        <v-card-text
          v-if="product && product.description"
          v-html="formattedDescription"
        />
        <v-divider class="text-disabled" v-if="product && product.description"/>
        <v-card-subtitle class="text-subtitle-1 pt-2">
          {{ $t('product-detail-view.details.cheapest.title') }}
        </v-card-subtitle>
        <v-card-text v-if="product && cheapestPrice">
          {{
            $t('product-detail-view.details.cheapest.content', {
              merchant: cheapestPrice ? cheapestPrice.merchant.name : '',
              product: productTitle,
              price: $n(cheapestPrice.price, 'currency'),
            })
          }}
        </v-card-text>
        <v-card-subtitle class="text-subtitle-1 pt-2" v-if="hasMerchants" :title="product ? product.name : undefined">
          {{ $t('product-detail-view.details.where-to-buy.title', {product: product.name}) }}
        </v-card-subtitle>
        <v-card-text v-if="hasMerchants">
          {{
            $t('product-detail-view.details.where-to-buy.content', {
              merchants: merchantNameList,
              product: product.name,
            })
          }}
        </v-card-text>
        <template v-if="product && !!product.tags.length">
          <v-divider class="mt-2 mb-2"/>
          <v-card-subtitle  class="text-subtitle-1 pt-2">
            {{ $t('product-detail-view.details.tags.title') }}
          </v-card-subtitle>
          <v-card-text>
            <v-chip size="small" class="ma-1" v-for="tag in product.tags" :key="tag">
              {{ tag }}
            </v-chip>
          </v-card-text>
        </template>
        <v-card-subtitle class="text-subtitle-1 pt-5" v-if="product && propertySections.length > 0">
          {{ $t('product-detail-view.details.additional', {product: product.name}) }}
        </v-card-subtitle>
        <v-divider class="mt-2 mb-2" v-if="product && propertySections.length > 0"/>
        <v-table v-if="product && propertySections.length > 0" style="font-size: 0.9rem">
          <tbody>
          <tr v-for="(section, sectionIdx) in propertySections ?? []" :key="sectionIdx">
            <td class="text-subtitle-1">{{ getPropertyTitle(section.property) }}</td>
            <td class="text-medium-emphasis">{{ section.text }}</td>
          </tr>
          </tbody>
        </v-table>
      </v-card>
    </v-col>
  </v-row>
</template>
<script setup lang="ts">
import SubHeading from "@/components/typography/sub-heading.vue";
import {Configuration, Product} from "@/lib/keys";
import {computed} from "vue";
import {defaultProductNameFilter} from "@/lib/filters";
import {build_product_description} from "@/lib/product-utils";
import {fancyJoin} from "@/lib/utils";
import {useI18n} from "vue-i18n";

interface Props {
  product: Product;
  prices: Configuration[];
}

const props = defineProps<Props>();

const cheapestPrice = computed(() => props.prices[0] ?? undefined)
const i18n = useI18n();


//Getters
const productTitle = computed(() => (!props.product || !props.product.name) ? 'N/A' : defaultProductNameFilter(props.product));

const propertySections = computed(() => build_product_description(props.product));

const hasMerchants = computed(() => props.product && props.product.merchants && props.product.merchants.length > 0)

const merchantNameList = computed(() => fancyJoin(
  props.product.merchants.map(m => m.name),
  ', ',
  ' og '
));

const formattedDescription = computed(() => {
  if (!props.product || !props.product.description) {
    return '';
  }
  if (props.product.description.endsWith('.')) {
    return props.product.description.trim();
  }

  return `${props.product.description}.`.trim();
});


const getPropertyTitle = (property: string) => {
  return i18n.t(property.replace('property.', 'properties.'));
}
</script>
